import { useCallback, useEffect, useState } from "react";
import { quotationListApi } from "features/quotation/api/quotationListApi";
import { QuotationRequest, QuoteDetail } from "features/quotation/types";

/**
 * Hook state for managing PDF visibility and quotation data.
 */
interface UseWheelTireEstResultPdfState {
  pdfVisible: boolean;
  quotation: QuoteDetail | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for managing wheel/tire estimation result PDF display and data fetching.
 * @param {QuotationRequest} [params] - Optional request parameters to filter quotations
 * @returns {object} Object containing:
 *   - pdfVisible: Boolean state for PDF modal visibility
 *   - setPdfVisible: Function to toggle PDF visibility
 *   - quotation: The fetched quotation data with PDF info
 *   - isLoading: Loading state while fetching data
 *   - error: Error object if API call fails
 *   - fetchQuotationWithPdf: Function to manually trigger API call
 */
const useWheelTireEstResultPdf = (params?: QuotationRequest) => {
  const [state, setState] = useState<UseWheelTireEstResultPdfState>({
    pdfVisible: false,
    quotation: null,
    isLoading: false,
    error: null,
  });

  /**
   * Fetches quotation data with PDF information from the API.
   */
  const fetchQuotationWithPdf = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const quotations = await quotationListApi(
        params || {},
        true, // withPdf flag
      );

      const quotationData = quotations.length > 0 ? quotations[0] : null;

      setState((prev) => ({
        ...prev,
        quotation: quotationData as unknown as QuoteDetail,
        isLoading: false,
      }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error occurred");
      setState((prev) => ({
        ...prev,
        error,
        isLoading: false,
      }));
    }
  }, [params?.quote_id]); // Only depend on quote_id, not the entire params object

  /**
   * Toggles PDF visibility state.
   */
  const setPdfVisible = useCallback((visible: boolean) => {
    setState((prev) => ({ ...prev, pdfVisible: visible }));
  }, []);

  /**
   * Fetch quotation data on component mount or when quote_id changes.
   */
  useEffect(() => {
    if (params?.quote_id) {
      fetchQuotationWithPdf();
    }
  }, [params?.quote_id]); // Only depend on quote_id to prevent unnecessary fetches

  return {
    pdfVisible: state.pdfVisible,
    setPdfVisible,
    quotation: state.quotation,
    isLoading: state.isLoading,
    error: state.error,
    fetchQuotationWithPdf,
  };
};

export default useWheelTireEstResultPdf;
